import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router';
import { Archive, Download, MoreVertical, Pencil, RotateCcw, Trash2, UserMinus, UserRoundCog } from 'lucide-react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import Collection from '@model-core/core/Collection';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { AuditDate } from '@/components/AuditDate';
import { Value } from '@/components/dashboard/DashboardParts';
import { AddTeamMemberDialog } from '@/components/teams/AddTeamMemberDialog';
import { teamColorOf } from '@/components/teams/teamColors';
import {
  DeleteTeamDialog,
  RemoveTeamMemberDialog,
  TeamFormDialog,
  TeamStatusDialog,
} from '@/components/teams/TeamDialogs';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFollowEntityCompany } from '@/hooks/useFollowEntityCompany';
import { useLoader } from '@/hooks/useLoader';
import { useOrgChartView } from '@/hooks/useOrgChartView';
import { getApiErrorMessage } from '@/lib/apiError';
import dayjs from '@/lib/dayjs';
import { downloadPng } from '@/lib/downloadImage';
import { slugify } from '@/lib/fileName';
import MemberModel from '@/models/entities/MemberModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import TeamModel from '@/models/entities/TeamModel';
import { COMPANY_ROLE, TEAM_MEMBER_STATUS, TEAM_ROLE } from '@/models/references/references';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { loadReferences, resolveReference } from '@/stores/useReferenceOptions';
import { composeTeam, sortDirection, type TeamComposition } from './teamOrgChart';
import { HierarchyOrgChart, OrgChartViewToggle, TreeOrgChart } from './OrgChartViews';

interface TeamDashboard {
  team: TeamModel;
  /** En haut de l'organigramme : Dirigeants et Assistants de la société (hors équipes) */
  direction: MemberModel[];
  composition: TeamComposition;
}

const BACK = { to: '/teams', label: 'Toutes les équipes' };

const DOWNLOAD_LABEL = "Télécharger l'organigramme (PNG)";

async function loadDashboard(id: string): Promise<TeamDashboard> {
  const [{ model: team }] = await Promise.all([
    new TeamModel({ id }).fetch({ silentError: true }),
    // Statut de l'équipe, rôles et statuts de ses membres, rôles dans la société (direction), lus par leur id
    loadReferences('teamStatuses', 'teamRoles', 'teamMemberStatuses', 'companyRoles'),
  ]);
  const notEmployees = await Promise.all(
    [COMPANY_ROLE.DIRECTOR, COMPANY_ROLE.ASSISTANT].map(
      async (role) => (await resolveReference('companyRoles', role)).id,
    ),
  );
  const [direction, members] = await Promise.all([
    new Collection(MemberModel)
      .setFilter('company', String(team.companyId))
      .setFilter('companyRole', notEmployees as never)
      .setSort('user.lastName')
      .list(),
    new Collection(TeamMemberModel)
      .setFilter('team', id)
      .setFilter(
        'teamMemberStatus',
        String((await resolveReference('teamMemberStatuses', TEAM_MEMBER_STATUS.ACTIVE)).id),
      )
      .list(),
  ]);

  // Nom de la direction, des membres et leur rôle dans la société
  await Promise.all([
    direction.collection.fetchResolvable('user'),
    members.collection.fetchResolvable('member', 'user'),
  ]);

  return {
    team,
    direction: sortDirection(direction.collection.models),
    composition: composeTeam(members.collection.models),
  };
}

/**
 * Fiche d'une équipe (/teams/{id}, Paramétrage Dirigeant) : en-tête (nom, description, statut ; Modifier, Archiver /
 * Réactiver, Supprimer) puis l'organigramme : la société, sa direction (membres qui ne sont pas simples Employés), le
 * manager de l'équipe puis ses équipiers, en vue Arbre (par défaut) ou Hiérarchie (prototype) (OrgChartViews.tsx).
 * Une équipe archivée se consulte sans modification.
 */
export function TeamDashboardPage() {
  const { id = '' } = useParams();

  // Remontée à chaque équipe : état et suivi de la société repartent de zéro
  return <TeamDashboardContent key={id} id={id} />;
}

function TeamDashboardContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const companies = useCompanyStore((state) => state.companies);
  const { data: dashboard, error, loaded, reload } = useLoader(id, loadDashboard);
  const [dialog, setDialog] = useState<'edit' | 'status' | 'delete' | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<TeamMemberModel | null>(null);
  const [view, setView] = useOrgChartView();
  // Téléchargement de l'organigramme : l'image est prise sur chartRef, rendu le temps de la capture en mode export
  const chartRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const OrgChart = view === 'tree' ? TreeOrgChart : HierarchyOrgChart;

  // La société de la sidebar suit l'équipe affichée ; en choisir une autre ramène à la liste de ses équipes
  useFollowEntityCompany(dashboard?.team.companyId, '/teams');

  if (!dashboard) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Équipe introuvable." />;
  }

  const { team } = dashboard;
  const companyName = companies.find((company) => company.id === team.companyId)?.name ?? '';
  const { manager } = dashboard.composition;
  const editable = team.isActive;

  /**
   * Organigramme en PNG (vue affichée) : rendu sans menus ni cases d'ajout, avec la société, l'équipe et la date,
   * capturé dans le navigateur, puis remis tel quel
   */
  const downloadChart = async () => {
    flushSync(() => setExporting(true));
    try {
      await downloadPng(chartRef.current!, `organigramme-${slugify(companyName)}-${slugify(team.name)}.png`);
    } catch {
      toast.error("L'organigramme n'a pas pu être généré, veuillez réessayer.");
    } finally {
      setExporting(false);
    }
  };

  const changeRole = async (teamMember: TeamMemberModel, teamRole: string) => {
    try {
      await new TeamMemberModel({ id: teamMember.id }).save(
        { teamRole: (await resolveReference('teamRoles', teamRole)).urn },
        { silentError: true },
      );
      toast.success(
        teamRole === TEAM_ROLE.MANAGER
          ? `${teamMember.fullName} est maintenant manager de l'équipe.`
          : `${teamMember.fullName} est maintenant équipier.`,
      );
      reload();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <DetailHeader
        back={BACK}
        label="Équipe"
        title={
          <span className="flex items-center gap-3">
            {/* Pastille de la couleur du tag */}
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: teamColorOf(team.color) }}
              data-testid="team-color"
              data-color={team.color || undefined}
              aria-hidden
            />
            {team.name}
            <span
              className={clsx(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
                team.isActive
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200',
              )}
            >
              {team.teamStatusLabel}
            </span>
          </span>
        }
        subtitle={team.description && <p className="mt-1 text-sm text-slate-500">{team.description}</p>}
        actions={
          <>
            <Button variant="outline" onClick={() => setDialog('edit')}>
              <Pencil className="w-4 h-4" />
              Modifier
            </Button>
            <Button variant="outline" onClick={() => setDialog('status')}>
              {team.isActive ? <Archive className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
              {team.isActive ? 'Archiver' : 'Réactiver'}
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDialog('delete')}
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </Button>
          </>
        }
        columns={2}
      >
        <Value label="Créée le">
          <AuditDate date={team.createdAt} by={team.createdBy} />
        </Value>
        <Value label="Date de dernière modification">
          <AuditDate date={team.updatedAt} by={team.updatedBy} />
        </Value>
      </DetailHeader>

      {/* Organigramme : vue du prototype (TeamDetail) ou arbre classique */}
      <section
        className="bg-card p-8 rounded-xl border border-slate-200 shadow-sm overflow-x-auto"
        aria-label="Organigramme"
      >
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-slate-900">{companyName}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <OrgChartViewToggle view={view} onChange={setView} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={DOWNLOAD_LABEL}
                  onClick={downloadChart}
                  disabled={exporting}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{DOWNLOAD_LABEL}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        {!editable && (
          <p className="mb-6 text-sm text-slate-500">
            Équipe archivée : elle n'a plus de membres. Réactivez-la pour lui en ajouter.
          </p>
        )}

        {/* Export : image claire même en mode nuit (theme-light, src/styles/dark.css), sur le fond blanc de downloadPng */}
        <div ref={chartRef} className={clsx(exporting && 'theme-light w-max bg-card text-foreground p-8')}>
          {exporting && (
            <div className="mb-8">
              <p className="text-sm text-slate-500">{companyName}</p>
              <p className="text-xl font-bold text-slate-900">Équipe {team.name}</p>
              <p className="text-xs text-slate-400">Organigramme au {dayjs().format('DD/MM/YYYY')}</p>
            </div>
          )}
          <OrgChart
            forExport={exporting}
            direction={dashboard.direction}
            composition={dashboard.composition}
            editable={editable}
            onAssignManager={() => setAdding(TEAM_ROLE.MANAGER)}
            onAddTeammate={() => setAdding(TEAM_ROLE.TEAMMATE)}
            memberActions={(teamMember) =>
              teamMember.isManager ? (
                <TeamMemberMenu
                  teamMember={teamMember}
                  onChangeRole={() => changeRole(teamMember, TEAM_ROLE.TEAMMATE)}
                  changeRoleLabel="Passer équipier"
                  onRemove={() => setRemoving(teamMember)}
                />
              ) : (
                <TeamMemberMenu
                  teamMember={teamMember}
                  onChangeRole={() => changeRole(teamMember, TEAM_ROLE.MANAGER)}
                  changeRoleLabel="Nommer manager"
                  changeRoleBlocker={manager ? `L'équipe a déjà un manager : ${manager.fullName}` : undefined}
                  onRemove={() => setRemoving(teamMember)}
                />
              )
            }
          />
        </div>
      </section>

      {dialog === 'edit' && (
        <TeamFormDialog
          team={team}
          companyId={team.companyId}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            toast.success(`L'équipe ${saved.name} a été modifiée.`);
            setDialog(null);
            reload();
          }}
        />
      )}
      {dialog === 'status' && (
        <TeamStatusDialog
          team={team}
          onClose={() => setDialog(null)}
          onChanged={() => {
            toast.success(
              team.isActive ? `L'équipe ${team.name} a été archivée.` : `L'équipe ${team.name} a été réactivée.`,
            );
            setDialog(null);
            reload();
          }}
        />
      )}
      {dialog === 'delete' && (
        <DeleteTeamDialog
          team={team}
          onClose={() => setDialog(null)}
          onDeleted={() => {
            toast.success(`L'équipe ${team.name} a été supprimée.`);
            navigate('/teams');
          }}
        />
      )}
      {adding && (
        <AddTeamMemberDialog
          team={team}
          manager={manager}
          initialRole={adding}
          onClose={() => setAdding(null)}
          onSaved={(personName) => {
            toast.success(`${personName} a rejoint l'équipe ${team.name}.`);
            setAdding(null);
            reload();
          }}
        />
      )}
      {removing && (
        <RemoveTeamMemberDialog
          team={team}
          teamMember={removing}
          onClose={() => setRemoving(null)}
          onRemoved={() => {
            toast.success(`${removing.fullName} a quitté l'équipe ${team.name}.`);
            setRemoving(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function TeamMemberMenu({
  teamMember,
  onChangeRole,
  changeRoleLabel,
  changeRoleBlocker,
  onRemove,
}: {
  teamMember: TeamMemberModel;
  onChangeRole: () => void;
  changeRoleLabel: string;
  changeRoleBlocker?: string;
  onRemove: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Actions pour ${teamMember.fullName}`}
        className="p-1.5 hover:bg-card rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onChangeRole} disabled={!!changeRoleBlocker} title={changeRoleBlocker}>
          <UserRoundCog className="w-4 h-4" />
          {changeRoleLabel}
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onSelect={onRemove}>
          <UserMinus className="w-4 h-4" />
          Retirer de l'équipe
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
